import { Input } from "@/components/ui/input";
import { addPushoverRecipient, deletePushoverRecipient, listPushoverRecipients, type PushoverRecipient } from "@/lib/pushover";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useRef, useState } from "react";
interface PushoverRecipientSelectorProps {
  label: string;
  description?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  connectorId?: string;
  /** Called with the connectorId of the selected recipient (used to auto-select the bot connector) */
  onConnectorSelect?: (connectorId: string) => void;
}
function recipientLabel(r: PushoverRecipient): string {
  if (r.label && r.label.trim()) return r.label;
  return `${r.userKey.slice(0, 6)}…${r.userKey.slice(-4)}`;
}
export default function PushoverRecipientSelector({
  label,
  description,
  value,
  onChange,
  required,
  connectorId,
  onConnectorSelect
}: PushoverRecipientSelectorProps) {
  const [recipients, setRecipients] = useState<PushoverRecipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Inline add-recipient form state
  const [addMode, setAddMode] = useState(false);
  const [newUserKey, setNewUserKey] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newDevice, setNewDevice] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const fetchRecipients = useCallback(() => {
    setLoading(true);
    listPushoverRecipients(connectorId, 50, 0).then(result => setRecipients(result.items)).catch(err => {
      console.error("Failed to load Pushover recipients", err);
      setRecipients([]);
    }).finally(() => setLoading(false));
  }, [connectorId]);
  useEffect(() => {
    fetchRecipients();
  }, [fetchRecipients]);
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setAddMode(false);
      }
    }
    if (isOpen) document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [isOpen]);
  const filtered = recipients.filter(r => {
    if (!query) return true;
    const q = query.toLowerCase();
    return r.userKey.toLowerCase().includes(q) || (r.label || "").toLowerCase().includes(q) || (r.device || "").toLowerCase().includes(q);
  });
  const selected = recipients.find(r_0 => r_0.userKey === value);
  async function handleRemove(id: string) {
    try {
      await deletePushoverRecipient(id);
      setRecipients(prev => prev.filter(r_1 => r_1.id !== id));
    } catch (err_0) {
      // toast layer not threaded here; log so operators can debug
      console.error("Failed to delete Pushover recipient", err_0);
    }
  }
  async function handleSaveRecipient(e_0: React.FormEvent) {
    e_0.preventDefault();
    setSaveError(null);
    if (!connectorId) {
      setSaveError("Select a Pushover connector first.");
      return;
    }
    if (!newUserKey.trim()) {
      setSaveError("User key is required.");
      return;
    }
    setSaving(true);
    try {
      const result_0 = await addPushoverRecipient({
        connectorId,
        userKey: newUserKey.trim(),
        label: newLabel.trim() || undefined,
        device: newDevice.trim() || undefined
      });
      setRecipients(prev_0 => [result_0.recipient, ...prev_0]);
      onChange(result_0.recipient.userKey);
      onConnectorSelect?.(result_0.recipient.connectorId);
      setAddMode(false);
      setNewUserKey("");
      setNewLabel("");
      setNewDevice("");
      setIsOpen(false);
    } catch (err_1) {
      setSaveError(err_1 instanceof Error ? err_1.message : "Failed to add recipient");
    } finally {
      setSaving(false);
    }
  }
  return <div className="w-full" ref={containerRef}>
      {label && <label className="field-label mb-1 block">
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled mb-1 text-xs">{description}</p>}

      <button type="button" onClick={() => setIsOpen(o => !o)} className={cn("border-ds-border-default bg-ds-surface-primary flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm", isOpen && "ring-2 ring-blue-300")}>
        <span className="truncate">
          {selected ? recipientLabel(selected) : value ? value : <span className="text-ds-text-muted">Select a recipient…</span>}
        </span>
        <span className="text-ds-text-muted text-xs">▾</span>
      </button>

      {isOpen && <div className="border-ds-border-default bg-ds-surface-primary mt-1 max-h-72 w-full overflow-auto rounded-md border shadow-lg">
          <div className="border-ds-border-default border-b p-2">
            <Input autoFocus placeholder="Search recipients…" value={query} onChange={e_1 => setQuery(e_1.target.value)} />
          </div>

          {loading && <div className="text-ds-text-muted p-3 text-sm">Loading…</div>}

          {!loading && filtered.map(r_2 => <div key={r_2.id} className="hover:bg-ds-surface-hover flex items-center px-3">
                <button type="button" onClick={() => {
          onChange(r_2.userKey);
          if (r_2.connectorId) onConnectorSelect?.(r_2.connectorId);
          setIsOpen(false);
          setQuery("");
        }} className={cn("flex flex-1 items-center gap-2 py-2 text-left text-sm", r_2.userKey === value ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" : "text-ds-text-primary")}>
                  <span className="min-w-0 flex-1 truncate">{recipientLabel(r_2)}</span>
                  {r_2.device && <span className="bg-ds-neutral-100 text-ds-text-subtle dark:bg-ds-neutral-700 shrink-0 rounded px-1.5 py-0.5 text-xs">
                      {r_2.device}
                    </span>}
                  <span className="caption-xs text-ds-text-muted shrink-0">
                    {r_2.userKey.slice(0, 6)}…{r_2.userKey.slice(-4)}
                  </span>
                </button>
                <button type="button" onClick={() => handleRemove(r_2.id)} className="text-ds-text-muted pl-2 text-xs hover:text-red-500" title="Remove recipient">
                  ✕
                </button>
              </div>)}

          {!loading && filtered.length === 0 && !addMode && <div className="text-ds-text-muted px-3 py-2 text-sm">No recipients yet.</div>}

          {!addMode && <button type="button" onClick={() => setAddMode(true)} className="hover:bg-ds-surface-hover border-ds-border-default text-ds-text-primary w-full border-t px-3 py-2 text-left text-sm">
              + Add a new recipient
            </button>}

          {addMode && <form onSubmit={handleSaveRecipient} className="border-ds-border-default space-y-2 border-t p-3">
              <p className="text-ds-text-muted text-xs leading-snug">
                The user key is shown at the top of{" "}
                <a href="https://pushover.net" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                  pushover.net
                </a>{" "}
                when you're logged in (looks like a 30-character mix of letters and numbers). For yourself, paste your
                own. For someone else, ask them for theirs. This is <strong>not</strong> the app token on the connector.
              </p>
              <Input placeholder="User key (e.g. uQiRzpo4DXghDmr9QzzfQu27cmVRsG)" value={newUserKey} onChange={e_2 => setNewUserKey(e_2.target.value)} autoFocus />
              <Input placeholder="Label (e.g. Alice's iPhone)" value={newLabel} onChange={e_3 => setNewLabel(e_3.target.value)} />
              <Input placeholder="Device (optional, default = all of this user's devices)" value={newDevice} onChange={e_4 => setNewDevice(e_4.target.value)} />
              {saveError && <div className="text-xs text-red-500">{saveError}</div>}
              <div className="flex items-center justify-end gap-2">
                <button type="button" onClick={() => setAddMode(false)} className="text-ds-text-muted px-2 py-1 text-sm">
                  Cancel
                </button>
                <button type="submit" disabled={saving || !connectorId} className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:opacity-50">
                  {saving ? "Saving…" : "Add"}
                </button>
              </div>
              {!connectorId && <p className="text-ds-text-muted text-xs">
                  Pick a Pushover connector on the node before adding recipients.
                </p>}
            </form>}
        </div>}
    </div>;
}